'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter, useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateProductQuestionMutation } from '@/generated/graphql'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { ModerationFeedback } from '../ModerationFeedback'
import {
  getCommunityQuestionFeedback,
  type CommunityQuestionFeedback,
} from '@/lib/communityQuestionFeedback'
import { Button } from '@/General/Button'
import { TextLink } from '@/General/TextLink'
import { toastEmitter } from '@/General/Toast'
import {
  QUESTION_MIN_LENGTH,
  QUESTION_MAX_LENGTH,
  ERROR_MESSAGES,
  CACHE_KEYS,
} from '@/communityQuestions'

interface QuestionFormData {
  question: string
}

interface QuestionFormProps {
  productId: number
  productSlug: string
  productName: string
}

export function QuestionForm({
  productId,
  productSlug,
  productName,
}: QuestionFormProps) {
  const [feedback, setFeedback] = useState<CommunityQuestionFeedback | null>(
    null
  )
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const returnUrl = searchParams.get('returnUrl') || `/product/${productSlug}`

  const buildSuccessUrl = (baseUrl: string) => {
    const [path, search] = baseUrl.split('?')
    const params = new URLSearchParams(search || '')
    params.set('questionPosted', 'true')
    return `${path}?${params.toString()}`
  }

  const createQuestionMutation = useCreateProductQuestionMutation({
    onSuccess: () => {
      // Invalidate only THIS product's cache (not all products)
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.PRODUCT, { slug: productSlug }],
      })
      // Invalidate only THIS product's questions (not all questions)
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.PRODUCT_QUESTIONS_INFINITE, { productId }],
      })
      // Show success toast
      toastEmitter.emit('toast', {
        title: 'Question posted!',
        body: 'Your question has been successfully posted to the community.',
        variant: 'success',
        duration: 5000,
      })
      // Return to the product page and show success message
      router.replace(buildSuccessUrl(returnUrl))
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setFocus,
    clearErrors,
  } = useForm<QuestionFormData>()

  const onSubmit = async (data: QuestionFormData) => {
    setFeedback(null)
    clearErrors()
    try {
      await createQuestionMutation.mutateAsync({
        productId,
        question: data.question,
      })
    } catch (error: unknown) {
      const moderationFeedback = getCommunityQuestionFeedback(error)
      if (moderationFeedback) {
        setFeedback(moderationFeedback)
        setFocus('question')
        return
      }
      // Check if it's a profanity filter error
      if (
        error instanceof Error &&
        error.message.includes('inappropriate language')
      ) {
        setError('question', {
          type: 'manual',
          message: ERROR_MESSAGES.PROFANITY_DETECTED,
        })
      } else {
        setError('root', {
          type: 'manual',
          message: ERROR_MESSAGES.SUBMISSION_FAILED,
        })
      }
    }
  }

  return (
    <>
      <p className="text-lg my-4">
        Ask the magic community for information about a product! Simply leave
        your question below for{' '}
        <TextLink href={`/product/${productSlug}`}>{productName}</TextLink> and
        hopefully someone from the community will answer!
      </p>

      <div>
        <ModerationFeedback feedback={feedback} />
        <ErrorBox
          errors={
            Object.values(errors)
              .map(error => error?.message)
              .filter(Boolean) as string[]
          }
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label
              htmlFor="question"
              className="block font-medium text-gray-700 mb-2"
            >
              Your question:
            </label>
            <div className="mt-1">
              <textarea
                id="question"
                aria-invalid={
                  feedback?.kind === 'guidelines' || !!errors.question
                }
                aria-describedby={
                  feedback ? 'community-post-feedback' : undefined
                }
                rows={6}
                className="shadow-sm block w-full border-gray-300 rounded-md resize-vertical"
                {...register('question', {
                  required: ERROR_MESSAGES.QUESTION_REQUIRED,
                  minLength: {
                    value: QUESTION_MIN_LENGTH,
                    message: ERROR_MESSAGES.QUESTION_TOO_SHORT,
                  },
                  maxLength: {
                    value: QUESTION_MAX_LENGTH,
                    message: ERROR_MESSAGES.QUESTION_TOO_LONG,
                  },
                })}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              color="green"
              size="lg"
              disabled={createQuestionMutation.isPending}
            >
              {createQuestionMutation.isPending
                ? 'Checking your post...'
                : 'Ask your question'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
